import React, { useEffect, useState } from 'react';

import Select from 'react-select';
import { useMediaQuery } from 'react-responsive';
import 'react-circular-progressbar/dist/styles.css';
import PhoneInput from 'react-phone-input-2';

import Text from 'common/components/Text/Text';
import { CommonHash } from 'app/types';
import { navigateTop } from 'common/utils';
import { GENDERS, RESIDENT_COUNTRIES, NATIONALITIES } from 'common/constants';
import UserFileUpload from './UserFileUpload';

import styles from './UserInformation.module.scss';
import 'react-phone-input-2/lib/style.css';

const gendersOptions = GENDERS.map((v: string, i: number) => ({ label: v, value: i }));
const residentOptions = RESIDENT_COUNTRIES.map((v: string, i: number) => ({ label: v, value: v }));
const nationalityOptions = NATIONALITIES.map((v: string, i: number) => ({ label: v, value: v }));
const passportOptions = ['I have a passport', "I don't have a passport"].map(
  (v: string, i: number) => ({ label: v, value: i })
);

interface CompProps {
  currentStep: number;
  setCurrentStep: (n: number) => void;
  answers: CommonHash;
  setAnswer: (h: CommonHash) => void;
  setSaveForm: (b: boolean) => void;
}

const PersonalForm = ({
  currentStep,
  setCurrentStep,
  answers,
  setAnswer,
  setSaveForm,
}: CompProps): React.ReactElement => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const setInputOption = (key: string, value: any) => {
    setAnswer((a: any) => ({ ...a, [key]: value }));
  };

  return (
    <div className={styles.stepContainer}>
      {!isMobile && (
        <Text size={20} weight={600} color="primary" inlineStyle={{ marginBottom: '35px' }}>
          Personal information
        </Text>
      )}
      <div className={styles.inputsContainter}>
        <div className={styles.inputGroup}>
          <Text
            size={16}
            weight={isMobile ? 500 : 600}
            inlineStyle={{ marginBottom: '12px' }}
            color="primary"
          >
            Full name
          </Text>
          <input
            className="form-control"
            type="text"
            onChange={(e) => setInputOption('name', e.target.value)}
            value={answers.name ? answers.name : ''}
          />
        </div>
        <div className={styles.inputGroup}>
          <Text
            size={16}
            weight={isMobile ? 500 : 600}
            inlineStyle={{ marginBottom: '12px' }}
            color="primary"
          >
            Gender
          </Text>
          <Select
            isSearchable
            classNamePrefix="react-select"
            className={styles.selectInput}
            options={gendersOptions}
            value={gendersOptions.find((o: any) => o.value === answers['gender'])}
            onChange={({ value }: any) => setInputOption('gender', value)}
          />
        </div>
      </div>
      <div className={styles.inputsContainter}>
        <div className={styles.inputGroup}>
          <Text
            size={16}
            weight={isMobile ? 500 : 600}
            inlineStyle={{ marginBottom: '12px' }}
            color="primary"
          >
            Date of birth
          </Text>
          <input
            className="form-control"
            type="date"
            onChange={(e) => setInputOption('birth_date', e.target.value)}
            value={answers.birth_date ? answers.birth_date : ''}
          />
        </div>
      </div>
      <div className={styles.inputsContainter}>
        <div className={styles.inputGroup}>
          <Text
            size={16}
            weight={isMobile ? 500 : 600}
            inlineStyle={{ marginBottom: '12px' }}
            color="primary"
          >
            Phone number
          </Text>
          <PhoneInput
            inputClass={styles.phoneInput}
            country={'gb'}
            countryCodeEditable={false}
            value={answers['phone'] ? answers['phone'].toString() : null}
            onChange={(e) => setInputOption('phone', '+' + e)}
          />
        </div>
        <div className={styles.inputGroup}>
          <Text
            size={16}
            weight={isMobile ? 500 : 600}
            inlineStyle={{ marginBottom: '12px' }}
            color="primary"
          >
            Country of residence
          </Text>
          <Select
            isSearchable
            classNamePrefix="react-select"
            className={styles.selectInput}
            options={residentOptions}
            value={residentOptions.find(
              (o: any) => o.label === (answers['residence_country'] || '')
            )}
            onChange={({ value }: any) => setInputOption('residence_country', value)}
          />
        </div>
        <div className={styles.inputGroup}>
          <Text
            size={16}
            weight={isMobile ? 500 : 600}
            inlineStyle={{ marginBottom: '12px' }}
            color="primary"
          >
            Nationality
          </Text>
          <Select
            isSearchable
            classNamePrefix="react-select"
            className={styles.selectInput}
            options={nationalityOptions}
            value={nationalityOptions.find((o: any) => o.label === answers['nationality'] || '')}
            onChange={({ value }: any) => setInputOption('nationality', value)}
          />
        </div>
        <div className={styles.inputGroup}>
          <Text
            size={16}
            weight={isMobile ? 500 : 600}
            inlineStyle={{ marginBottom: '12px' }}
            color="primary"
          >
            Passport status
          </Text>
          <Select
            isSearchable
            classNamePrefix="react-select"
            className={styles.selectInput}
            options={passportOptions}
            value={passportOptions.find((o: any) => o.value === answers['passport_status'])}
            onChange={({ value }: any) => setInputOption('passport_status', value)}
          />
        </div>
      </div>
      <UserFileUpload setAnswer={setAnswer} fileType="passport" answers={answers} />
      {isMobile && (
        <>
          <button
            className={styles.nextStepButton}
            onClick={() => {
              setCurrentStep(currentStep + 1);
              navigateTop();
            }}
            disabled={isLoading}
          >
            Next
          </button>
          <button
            className={styles.saveButton}
            onClick={() => {
              setSaveForm(true);
              setIsLoading(true);
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className={`spinner-border ${styles.loading}`} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            ) : (
              'Save & Leave'
            )}
          </button>
        </>
      )}
    </div>
  );
};

export default PersonalForm;
